/**
 * TenantStorage - 租戶隔離的 LocalStorage 封裝
 *
 * 為所有 localStorage 操作自動加上 tenantId 前綴，確保不同租戶的資料不會互相干擾。
 *
 * 使用方式：
 * ```typescript
 * const tenantId = getTenantIdFromUrl();
 * const storage = new TenantStorage(tenantId);
 * storage.setItem('cart', cartData);
 * const cart = storage.getItem('cart');
 * ```
 */
export class TenantStorage {
  private tenantId: string;
  private separator = '::'; // 分隔符號，避免與內容混淆

  constructor(tenantId: string) {
    if (!tenantId || tenantId.trim() === '') {
      throw new Error('Tenant ID is required for storage initialization');
    }
    this.tenantId = tenantId.trim();
  }

  /**
   * 產生帶有 namespace 的 key
   * 例如: "shopA::cart_items"
   */
  private _getKey(key: string): string {
    return `${this.tenantId}${this.separator}${key}`;
  }

  /**
   * 儲存資料到 localStorage
   * 自動將值轉換為 JSON 字串
   */
  setItem(key: string, value: unknown): void {
    const namespacedKey = this._getKey(key);
    try {
      // 建議存成 JSON 字串，方便處理物件
      const stringValue = JSON.stringify(value);
      localStorage.setItem(namespacedKey, stringValue);
    } catch (error) {
      console.error(`[TenantStorage] 儲存失敗: ${key}`, error);
      throw new Error(
        `無法儲存資料到 localStorage: ${error instanceof Error ? error.message : String(error)}`
      );
    }
  }

  /**
   * 從 localStorage 讀取資料
   * 自動解析 JSON 字串
   */
  getItem<T = unknown>(key: string): T | null {
    const namespacedKey = this._getKey(key);
    const value = localStorage.getItem(namespacedKey);

    if (value === null) {
      return null;
    }

    try {
      return JSON.parse(value) as T;
    } catch (e) {
      // 如果解析失敗，可能是舊格式的純字串，直接返回
      console.warn(`[TenantStorage] JSON 解析失敗，返回原始字串: ${key}`, e);
      return value as unknown as T;
    }
  }

  /**
   * 從 localStorage 移除資料
   */
  removeItem(key: string): void {
    const namespacedKey = this._getKey(key);
    localStorage.removeItem(namespacedKey);
  }

  /**
   * 清除「該租戶」所有的資料，但不影響其他租戶
   */
  clearTenantData(): void {
    const prefix = `${this.tenantId}${this.separator}`;
    const keysToRemove: string[] = [];

    // 收集所有屬於該租戶的 key
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        keysToRemove.push(key);
      }
    }

    // 移除所有收集到的 key
    keysToRemove.forEach(key => {
      localStorage.removeItem(key);
    });

    console.log(
      `[TenantStorage] 已清除租戶 ${this.tenantId} 的 ${keysToRemove.length} 筆資料`
    );
  }

  /**
   * 取得該租戶的所有 key（不含前綴）
   */
  getAllKeys(): string[] {
    const prefix = `${this.tenantId}${this.separator}`;
    const keys: string[] = [];

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(prefix)) {
        // 移除前綴，返回原始 key
        const originalKey = key.substring(prefix.length);
        keys.push(originalKey);
      }
    }

    return keys;
  }
}
